import React, { useState } from 'react';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Label } from '../ui/label.jsx';
import { Textarea } from '../ui/textarea.jsx';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Building, 
  Award,
  TrendingUp,
  DollarSign,
  Target,
  Users,
  Eye,
  EyeOff,
  CheckCircle,
  Globe
} from 'lucide-react';

const Signup = () => {
  const [accountType, setAccountType] = useState('business'); // 'business' or 'affiliate'
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLoggedInMessage, setShowLoggedInMessage] = useState(false);

  // Check if user is already logged in
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      setShowLoggedInMessage(true);
    }
  }, []);
  const [formData, setFormData] = useState({
    // Common fields
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    
    // Affiliate-specific fields
    business_name: '',
    website: '',
    social_media: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    },
    target_audience: '',
    marketing_experience: '',
    referral_cookie_duration: 30
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.email || !formData.password || !formData.full_name) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      let endpoint = '';
      let payload = {};

      if (accountType === 'affiliate') {
        // Use affiliate signup endpoint
        endpoint = '/api/affiliates/signup';
        payload = formData;
      } else {
        // Use regular user signup endpoint (we need to create this)
        endpoint = '/api/auth/register';
        payload = {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          company_name: formData.company_name
        };
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Don't store token - user needs to verify email first
        // Cookies.set('enakid_token', result.access_token, { expires: 7 });
        // Cookies.set('enakid_user', JSON.stringify(result.user), { expires: 7 });
        
        // Redirect to verification pending page
        toast.success('Account created! Please check your email to verify your account.');
        setTimeout(() => {
          window.location.href = `/verification-pending?email=${encodeURIComponent(formData.email)}`;
        }, 1500);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create account');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Show message for already logged in users
  if (showLoggedInMessage) {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <CardTitle>Already Logged In</CardTitle>
            <CardDescription>
              You're already logged in as {user.full_name || user.email}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={() => window.location.href = '/dashboard'} 
              className="w-full"
            >
              Go to Dashboard
            </Button>
            <Button 
              onClick={() => window.location.href = '/affiliate'} 
              variant="outline" 
              className="w-full"
            >
              View Affiliate Dashboard
            </Button>
            <Button 
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                setShowLoggedInMessage(false);
              }} 
              variant="outline" 
              className="w-full"
            >
              Sign Out & Create New Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <img 
            src="/TavPlans_Logo.png" 
            alt="TavPlans Logo" 
            className="h-20 w-20 object-contain mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Join TavPlans
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose how you'd like to get started with TavPlans's financial modeling platform
          </p>
        </div>

        {/* Account Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card 
            className={`cursor-pointer transition-all ${
              accountType === 'business' 
                ? 'ring-2 ring-blue-500 bg-blue-50' 
                : 'hover:shadow-lg'
            }`}
            onClick={() => setAccountType('business')}
          >
            <CardHeader className="text-center">
              <Building className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <CardTitle>Business Planning Account</CardTitle>
              <CardDescription>
                Create financial models, scenarios, and business plans for your company
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Financial modeling & forecasting
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Scenario planning & analysis
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Business intelligence dashboard
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Export & reporting tools
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all ${
              accountType === 'affiliate' 
                ? 'ring-2 ring-purple-500 bg-purple-50' 
                : 'hover:shadow-lg'
            }`}
            onClick={() => setAccountType('affiliate')}
          >
            <CardHeader className="text-center">
              <Award className="h-12 w-12 text-purple-600 mx-auto mb-4" />
              <CardTitle>Affiliate Partner Account</CardTitle>
              <CardDescription>
                Earn commissions by referring businesses to TavPlans's platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  20-35% recurring commissions
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Real-time tracking & analytics
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Marketing materials & support
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Tier-based reward system
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Registration Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center">
              <UserPlus className="h-5 w-5 mr-2" />
              Create Your {accountType === 'affiliate' ? 'Affiliate' : 'Business'} Account
            </CardTitle>
            <CardDescription>
              {accountType === 'affiliate' 
                ? 'Join our affiliate program and start earning commissions'
                : 'Get started with TavPlans\'s financial modeling platform'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Account Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Account Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={(e) => handleInputChange('full_name', e.target.value)}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="At least 6 characters"
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      placeholder="Your company name"
                    />
                  </div>
                </div>
              </div>

              {/* Affiliate-specific fields */}
              {accountType === 'affiliate' && (
                <>
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Business Information</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="business_name">Business/Brand Name</Label>
                        <Input
                          id="business_name"
                          type="text"
                          value={formData.business_name}
                          onChange={(e) => handleInputChange('business_name', e.target.value)}
                          placeholder="Your business name"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="website">Website URL</Label>
                        <Input
                          id="website"
                          type="url"
                          value={formData.website}
                          onChange={(e) => handleInputChange('website', e.target.value)}
                          placeholder="https://yourwebsite.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center">
                      <Target className="h-5 w-5 mr-2" />
                      Marketing Information (Optional)
                    </h3>
                    
                    <div>
                      <Label htmlFor="target_audience">Target Audience</Label>
                      <Textarea
                        id="target_audience"
                        value={formData.target_audience}
                        onChange={(e) => handleInputChange('target_audience', e.target.value)}
                        placeholder="Describe your target audience (e.g., small business owners, startups, consultants)"
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="marketing_experience">Marketing Experience</Label>
                      <Textarea
                        id="marketing_experience"
                        value={formData.marketing_experience}
                        onChange={(e) => handleInputChange('marketing_experience', e.target.value)}
                        placeholder="Tell us about your marketing experience and how you plan to promote TavPlans"
                        rows={3}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Submit Button */}
              <div className="space-y-4">
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                  size="lg"
                >
                  {loading 
                    ? 'Creating Account...' 
                    : `Create ${accountType === 'affiliate' ? 'Affiliate' : 'Business'} Account`
                  }
                </Button>
              </div>

              <div className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <a href="/login" className="text-blue-600 hover:underline">
                  Sign in here
                </a>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;